import { useState } from "preact/hooks";
import { finishUpload, putMaster, startUpload, type UploadTicket } from "./api.ts";
import { clock } from "./audio.ts";
import type { Recorded } from "./navigation.ts";
import { Button, Callout, Field, ScreenHead, ScreenTitle } from "./ui.tsx";
import { useAction } from "./use-action.ts";

interface Props {
  category: string;
  recording: Recorded;
  onBack: () => void;
  onAgain: () => void;
  /** Handed the stored message's id once the phone version exists. */
  onSaved: (id: string) => void;
}

/**
 * Listen back, name it, store it. The recording is saved disabled, so the
 * staffer can hear it on the phone's own terms before callers can.
 */
export function ReviewRecordingScreen({
  category,
  recording,
  onBack,
  onAgain,
  onSaved,
}: Props) {
  const [title, setTitle] = useState("");
  const { busy, error, run } = useAction();

  function save(event: Event): void {
    event.preventDefault();
    void run(async () => {
      if (recording.blob.size === 0) throw new Error("that recording is empty");
      const ticket: UploadTicket = await startUpload({
        category,
        title: title.trim(),
        contentType: recording.contentType,
      });
      if (recording.blob.size > ticket.maxBytes) {
        throw new Error("that recording is too long to store; record a shorter one");
      }
      await putMaster(ticket, recording.blob);
      await finishUpload({
        category,
        id: ticket.id,
        title: title.trim(),
        masterKey: ticket.masterKey,
        active: false,
      });
      URL.revokeObjectURL(recording.url);
      onSaved(ticket.id);
    });
  }

  return (
    <>
      <ScreenHead onBack={onBack} disabled={busy} />
      <div class="review">
        <div>
          <ScreenTitle>Review your recording</ScreenTitle>
          <div class="player player-block">
            {/* biome-ignore lint/a11y/useMediaCaption: a recording of a voice has no caption track */}
            <audio controls src={recording.url} />
            <span class="meta">{clock(Math.round(recording.seconds))} long</span>
          </div>
          <Button full disabled={busy} onClick={onAgain}>
            Record again
          </Button>
        </div>

        <form onSubmit={save}>
          <Field id="recording-title" label="Title">
            <input
              id="recording-title"
              type="text"
              placeholder="Grandpa's fishing joke"
              value={title}
              disabled={busy}
              onInput={(event) => setTitle((event.target as HTMLInputElement).value)}
            />
          </Field>
          <Button
            tone="primary"
            full
            type="submit"
            busy={busy}
            busyLabel="Saving…"
            disabled={title.trim() === ""}
          >
            Save message
          </Button>

          {busy && (
            <div class="progress-row">
              {/* The transcode happens inside one request, so there is no
                  percentage to report. The bar spans the line under its label,
                  where a short one would read as a fill level. */}
              <span class="meta">Preparing for the phone…</span>
              <div
                class="progress"
                role="progressbar"
                aria-label="Preparing for the phone"
              />
            </div>
          )}
          {busy && (
            <Callout tone="info" title="Callers hear a converted copy">
              It may sound a little different from what you recorded.
            </Callout>
          )}
          {error && <Callout tone="bad" title={error} />}
        </form>
      </div>
    </>
  );
}
